import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Repeat } from "lucide-react";
import { addDaysDDMMYYYY } from "@/lib/format";
import { CapabilityNotice } from "@/components/bse/BseUatForm";
import { useBseCapability } from "@/components/BseCapabilityStatus";

function SchemeSearchField({ label, value, onSelect, testIdPrefix, filterKey }) {
  const [search, setSearch] = useState(value?.scheme_name || "");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(value || null);

  useEffect(() => {
    if (!search || selected?.scheme_name === search) return;
    const t = setTimeout(() => {
      const params = { search, page_size: 6 };
      if (filterKey) params[filterKey] = true;
      clientApi.get("/schemes", { params }).then((r) => setResults(r.data.items));
    }, 300);
    return () => clearTimeout(t);
  }, [search, selected, filterKey]);

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        placeholder="Search scheme..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setSelected(null); onSelect(null); }}
        data-testid={`${testIdPrefix}-search-input`}
      />
      {results.length > 0 && !selected && (
        <div className="max-h-40 overflow-y-auto rounded-md border border-[var(--glass-border)]">
          {results.map((s) => (
            <button
              type="button" key={s.scheme_code}
              onClick={() => { setSelected(s); setSearch(s.scheme_name); setResults([]); onSelect(s); }}
              className="block w-full p-2 text-left text-xs hover:bg-[var(--surface-2)]"
              data-testid={`${testIdPrefix}-option-${s.scheme_code}`}
            >
              {s.scheme_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SipNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [planType, setPlanType] = useState("sip");
  const [scheme, setScheme] = useState(null);
  const [toScheme, setToScheme] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [holdingKey, setHoldingKey] = useState("");
  const [uccModes, setUccModes] = useState({ physical: true, demat: true });
  const [amount, setAmount] = useState("");
  const [units, setUnits] = useState("");
  const [frequency, setFrequency] = useState("MONTHLY");
  const [installments, setInstallments] = useState(12);
  const [mandates, setMandates] = useState([]);
  const [mandateId, setMandateId] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [folio, setFolio] = useState("");
  const [demat, setDemat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const capability = useBseCapability("systematic_plans");

  useEffect(() => {
    const code = params.get("scheme");
    if (code) clientApi.get(`/schemes/${code}`).then((r) => setScheme(r.data));
    clientApi.get("/mandates").then((r) => setMandates(r.data.filter((m) => m.status === "APPROVED" && m.bse2?.exch_mandate_id)));
    clientApi.get("/portfolio/holdings").then((r) => setHoldings((r.data.holdings || []).filter((h) => Number(h.units) > 0)));
    clientApi.get("/bse/status").then((r) => {
      const active = (r.data.uccs || []).filter((item) => (item.status || "ACTIVE") === "ACTIVE");
      if (active.length) setUccModes({
        physical: active.some((item) => item.allows_physical || ["PHYSICAL", "BOTH"].includes(item.holding_mode)),
        demat: active.some((item) => item.allows_demat || ["DEMAT", "BOTH"].includes(item.holding_mode)),
      });
    }).catch(() => {});
  }, [params]);

  const applySourceHolding = (row) => {
    if (!row) { setScheme(null); setHoldingKey(""); return; }
    setScheme(row);
    setHoldingKey(row.holding_key || row.scheme_code);
    setFolio(row.folio_no || "");
    setDemat(row.holding_mode === "DEMAT");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scheme) { toast.error("Please select a scheme"); return; }
    if (planType === "stp" && !toScheme) { toast.error("Please select a destination scheme for STP"); return; }
    if (planType === "xsip" && !mandateId) { toast.error("Select an active BSE mandate before registering an XSIP"); return; }
    if (planType === "swp" && Boolean(amount) === Boolean(units)) { toast.error("Enter either a withdrawal amount or withdrawal units, not both"); return; }
    setLoading(true);
    try {
      let submitted;
      if (planType === "sip") {
        const { data } = await clientApi.post("/sips", {
          scheme_code: scheme.scheme_code, start_date: addDaysDDMMYYYY(30), frequency_type: frequency,
          installment_amount: Number(amount), no_of_installments: Number(installments), sip_type: "01",
          demat,
        });
        submitted = { kind: "SIP", regId: data.sip_reg_id, bse2: data.bse2 };
      } else if (planType === "xsip") {
        const { data } = await clientApi.post("/xsips", {
          scheme_code: scheme.scheme_code, start_date: addDaysDDMMYYYY(45), frequency_type: frequency,
          installment_amount: Number(amount), no_of_installments: Number(installments), mandate_id: mandateId, sip_type: "01",
          demat,
        });
        submitted = { kind: "XSIP", regId: data.xsip_reg_id, bse2: data.bse2 };
      } else if (planType === "stp") {
        const { data } = await clientApi.post("/stp", {
          from_scheme_code: scheme.scheme_code, to_scheme_code: toScheme.scheme_code,
          start_date: addDaysDDMMYYYY(30), frequency_type: frequency,
          installment_amount: Number(amount), no_of_transfers: Number(installments),
          folio_no: folio, demat,
        });
        submitted = { kind: "STP", regId: data.stp_reg_no, bse2: data.bse2 };
      } else if (planType === "swp") {
        const { data } = await clientApi.post("/swp", {
          scheme_code: scheme.scheme_code, start_date: addDaysDDMMYYYY(30), frequency_type: frequency,
          installment_amount: amount ? Number(amount) : undefined, installment_units: units ? Number(units) : undefined,
          number_of_withdrawals: Number(installments), bank_account_no: accountNo,
          folio_no: folio, demat,
        });
        submitted = { kind: "SWP", regId: data.swp_reg_no, bse2: data.bse2 };
      }
      const pending = ["SUBMITTING", "UNKNOWN"].includes(submitted?.bse2?.status);
      setResult({ ...submitted, confirmationPending: pending });
      if (pending) toast.warning(submitted.bse2?.message || "BSE confirmation is pending. Do not submit this plan again.");
      else toast.success(submitted?.bse2?.message || `${planType.toUpperCase()} accepted by BSE.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const accepted = result.bse2?.status === "SUBMITTED";
    const ResultIcon = accepted ? CheckCircle2 : AlertTriangle;
    return (
      <ClientLayout>
        <div className="mx-auto max-w-lg py-10 text-center">
          <ResultIcon className={`mx-auto h-16 w-16 ${accepted ? "text-[var(--cta)]" : "text-amber-600"}`} />
          <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">{result.kind} {accepted ? "accepted by BSE" : "confirmation pending"}</h1>
          <p className="mt-2 text-sm text-[var(--ink-muted)]">Local audit reference {result.regId}</p>
          {result.bse2?.reg_no && <p className="mt-1 text-sm">BSE registration {result.bse2.reg_no}</p>}
          {result.bse2?.auth_link && <a href={result.bse2.auth_link} target="_blank" rel="noreferrer" className="mt-4 block text-sm font-medium text-[var(--cta)] underline">Complete authentication on BSE →</a>}
          {result.bse2 && <p className="mt-2 text-xs text-[var(--ink-muted)]">{result.bse2.message}</p>}
          <Button className="mt-6 rounded-full bg-[var(--cta)]" onClick={() => navigate("/sips")} data-testid="sip-result-view-button">View Systematic Plans</Button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <Repeat className="h-6 w-6 text-[var(--cta)]" />
          <h1 className="font-heading text-2xl text-[var(--brand)]">Start a new systematic plan</h1>
        </div>
        <Card className="glass-card border-0">
          <CardHeader>
            <Tabs value={planType} onValueChange={(v) => { setPlanType(v); setScheme(null); setToScheme(null); setHoldingKey(""); setFolio(""); }}>
              <TabsList>
                <TabsTrigger value="sip" data-testid="sip-type-sip">SIP</TabsTrigger>
                <TabsTrigger value="xsip" data-testid="sip-type-xsip">XSIP</TabsTrigger>
                <TabsTrigger value="stp" data-testid="sip-type-stp">STP</TabsTrigger>
                <TabsTrigger value="swp" data-testid="sip-type-swp">SWP</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <CapabilityNotice capability={capability} title="BSE systematic-plan registration" />
              {(planType === "sip" || planType === "xsip") && <div className="space-y-1.5"><Label>Holding mode</Label><select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={demat ? "DEMAT" : "PHYSICAL"} onChange={(e) => setDemat(e.target.value === "DEMAT")}><option value="PHYSICAL" disabled={!uccModes.physical}>Physical / statement of account</option><option value="DEMAT" disabled={!uccModes.demat}>Demat</option></select></div>}
              {(planType === "stp" || planType === "swp") ? <div className="space-y-1.5">
                <Label>{planType === "stp" ? "Transfer from allotted holding" : "Withdraw from allotted holding"}</Label>
                <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={holdingKey} onChange={(e) => applySourceHolding(holdings.find((h) => (h.holding_key || h.scheme_code) === e.target.value))}>
                  <option value="">Select an existing holding</option>{holdings.map((h) => <option key={h.holding_key || h.scheme_code} value={h.holding_key || h.scheme_code}>{h.scheme_name} · folio {h.folio_no || "—"} · {h.holding_mode || "PHYSICAL"} · {h.units} units</option>)}
                </select>
                {scheme && <div className="grid grid-cols-2 gap-3 rounded-xl bg-[var(--surface-2)] p-3 text-xs"><div><span className="text-[var(--ink-muted)]">Folio</span><div className="font-medium">{folio || "—"}</div></div><div><span className="text-[var(--ink-muted)]">Mode</span><div className="font-medium">{demat ? "DEMAT" : "PHYSICAL"}</div></div></div>}
                {!holdings.length && <p className="text-xs text-amber-700">No allotted holdings are available. Sync completed BSE orders first.</p>}
              </div> : <SchemeSearchField label="Scheme" value={scheme} onSelect={setScheme} testIdPrefix="sip-scheme" filterKey="sip_flag" />}
              {planType === "stp" && (
                <SchemeSearchField label="Transfer to scheme" value={toScheme} onSelect={setToScheme} testIdPrefix="stp-to-scheme" filterKey="switch_flag" />
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{planType === "swp" ? "Withdrawal amount (optional if units given)" : "Installment amount"}</Label>
                  <Input type="number" required={planType !== "swp"} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="sip-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>No. of {planType === "stp" ? "transfers" : planType === "swp" ? "withdrawals" : "installments"}</Label>
                  <Input type="number" required value={installments} onChange={(e) => setInstallments(e.target.value)} data-testid="sip-installments-input" />
                </div>
              </div>
              {planType === "swp" && (
                <div className="space-y-1.5">
                  <Label>OR withdrawal units</Label>
                  <Input type="number" value={units} onChange={(e) => setUnits(e.target.value)} data-testid="swp-units-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Frequency</Label>
                <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={frequency} onChange={(e) => setFrequency(e.target.value)} data-testid="sip-frequency-select">
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>

              {planType === "swp" && (
                <div className="space-y-1.5">
                  <Label>Bank account for payout</Label>
                  <Input required value={accountNo} onChange={(e) => setAccountNo(e.target.value)} data-testid="swp-account-input" />
                </div>
              )}

              {planType === "xsip" && (
                <div className="space-y-3 rounded-xl border border-[var(--glass-border)] p-3">
                  <Label>Mandate</Label>
                  <p className="-mt-1 text-xs text-[var(--ink-muted)]">Per BSE mandate registration lead time, your XSIP will start on {addDaysDDMMYYYY(45)} (45 days from today).</p>
                  {mandates.length > 0 ? (
                    <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={mandateId} onChange={(e) => setMandateId(e.target.value)} data-testid="sip-mandate-select">
                      <option value="">Select active BSE mandate</option>
                      {mandates.map((m) => <option key={m.mandate_id} value={m.mandate_id}>Mandate #{m.mandate_id} (limit ₹{m.amount})</option>)}
                    </select>
                  ) : (
                    <p className="text-xs text-amber-700">No active BSE mandate is available. Register and authenticate a mandate on the Mandates page, then return here.</p>
                  )}
                </div>
              )}

              <Button type="submit" disabled={loading || !capability?.enabled} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="sip-submit-button">
                {loading ? "Registering..." : `Register ${planType.toUpperCase()}`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
